import { collection, named, num, text, toggle, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { look, paint, typeface } from './styleTokens';

// Products hanging on a rail, as in a shop: side-on until the pointer reaches one, which turns to face the shopper
// while its neighbours make room, and comes forward when pressed. What shows under the garment that came forward is
// whatever this element holds, so its name, its prices and its buttons stay ordinary elements a merchant edits one by one.

/** How the rail ends: running out of both sides of the window, or stopping in two caps with the clothes between them. */
const RAIL_ENDS: readonly Choice[] = [['window', 'Runs off both sides of the window'], ['caps', 'Stops in two caps']];

/** How tall a garment's picture may hang, in pixels on a desktop. A phone draws seven tenths of this. */
const MIN_HEIGHT = 240;
const MAX_HEIGHT = 720;

/** How much room each garment takes along the rail, as a share of its height. */
const MIN_ROOM = 20;
const MAX_ROOM = 70;

/** The most products a rail may hang, matching the bound the read itself enforces. */
const MAX_GARMENTS = 24;

/** The rail: one product per garment, read from the store, with the elements inside it shown under a pressed one. */
export const productRailElement = element('product-rail', {
  label: 'Clothes rail',
  description: 'Your products hanging on a rail. Each turns to face the shopper under the pointer and comes forward when pressed. A picture whose description says “side” or “back” is used for that view; with none, the first picture itself turns.',
  category: 'Other',
  icon: 'shirt',
  props: [
    { ...named('source', 'Products from', [['collection', 'A collection, or all products newest first'], ['best-selling', 'Best sellers'], ['on-sale', 'Products on sale']]), help: 'Best sellers are what sold most in the last 90 days.' },
    { ...collection('collection', 'Collection', 'All products'), help: 'Which of your collections hang on this rail.' },
    { ...num('limit', 'How many to hang', 1, MAX_GARMENTS), help: 'A rail wider than the window moves along under the mouse, and a phone swipes it.' },
    { ...num('garmentHeight', 'Garment height', MIN_HEIGHT, MAX_HEIGHT), help: 'In pixels on a desktop. A phone draws seven tenths of this.' },
    { ...num('garmentRoom', 'Room per garment', MIN_ROOM, MAX_ROOM), help: 'As a share of the height, so garments hang closer or further apart.' },
    named('railEnds', 'The rail', RAIL_ENDS),
    { ...text('restLine', 'Words under the rail'), help: 'Shown until the pointer reaches a garment, when its name and price take their place.' },
    { ...toggle('showTags', 'Hang a tag on each garment'), help: 'Says the discount, how few are left, or that it is new, from your catalogue.' },
    toggle('sampleContent', 'Show theme samples when empty')
  ],
  defaults: {
    props: { collection: '', limit: 10, garmentHeight: 460, garmentRoom: 33, railEnds: 'window', restLine: '', showTags: true },
    styles: look({ width: '100%', fontFamily: typeface('body'), color: paint('heading') })
  }
});
